'use client';

import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import ComparisonCard from './ComparisonCard';

function MobileComparisonSlider({ variants, showStandardEquipment }) {
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <div className="flex flex-col items-center w-full gap-4 pt-2 pb-4" dir="ltr">
      <div className="w-full h-auto">
        <Swiper
          onSwiper={setSwiperInstance}
          onSlideChange={(s) => setActiveIndex(s.activeIndex)}
          spaceBetween={14}
          slidesPerView={1.12}
          centeredSlides={true}
          initialSlide={0}
          className="w-full h-full pt-16 pb-6"
        >
          {variants.map((car) => (
            <SwiperSlide key={car.id} className="h-auto">
              <ComparisonCard
                car={car}
                showStandardEquipment={showStandardEquipment}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Mobile Navigation Bar matching Figma Sample (< • • ⦿ • >) adapted for light theme */}
      <div className="flex items-center justify-center gap-3 pt-2 text-gray-800">
        <button
          onClick={() => swiperInstance?.slidePrev()}
          className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:text-gray-800 hover:border-gray-500 transition-all cursor-pointer bg-white shadow-sm"
          aria-label="اسلاید قبلی"
        >
          <FaChevronLeft className="text-[10px]" />
        </button>

        <div className="flex items-center gap-2 px-1">
          {variants.map((_, idx) => (
            <button
              key={idx}
              onClick={() => swiperInstance?.slideTo(idx)}
              className="cursor-pointer transition-all duration-300 flex items-center justify-center p-1"
              aria-label={`اسلاید ${idx + 1}`}
            >
              {activeIndex === idx ? (
                <span className="w-3.5 h-3.5 rounded-full border border-gray-900 flex items-center justify-center">
                  <span className="w-1 h-1 bg-gray-900 rounded-full" />
                </span>
              ) : (
                <span className="w-1.5 h-1.5 bg-gray-300 rounded-full hover:bg-gray-500" />
              )}
            </button>
          ))}
        </div>

        <button
          onClick={() => swiperInstance?.slideNext()}
          className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:text-gray-800 hover:border-gray-500 transition-all cursor-pointer bg-white shadow-sm"
          aria-label="اسلاید بعدی"
        >
          <FaChevronRight className="text-[10px]" />
        </button>
      </div>
    </div>
  );
}

export default function ModelComparison({ variants }) {
  const [showStandardEquipment, setShowStandardEquipment] = useState(false);

  if (!variants || variants.length === 0) return null;

  return (
    <div className="w-full max-w-[1700px] mx-auto px-4 md:px-8 py-10">

      {/* Top Toggle */}
      <div className="flex justify-center mb-12">
        <div className="flex bg-white rounded-small-button-mob md:rounded-small-button-desk p-1 border border-gray-200 shadow-sm" dir="rtl">
          <button
            onClick={() => setShowStandardEquipment(false)}
            className={`px-8 py-2.5 rounded-small-button-mob md:rounded-small-button-desk text-sm font-semibold transition-all ${!showStandardEquipment
                ? 'bg-gray-100 text-gray-900 shadow-sm'
                : 'bg-transparent text-gray-500 hover:text-gray-700'
              }`}
          >
            مشخصات فنی
          </button>
          <button
            onClick={() => setShowStandardEquipment(true)}
            className={`px-8 py-2.5 rounded-small-button-mob md:rounded-small-button-desk text-sm font-semibold transition-all ${showStandardEquipment
                ? 'bg-gray-100 text-gray-900 shadow-sm'
                : 'bg-transparent text-gray-500 hover:text-gray-700'
              }`}
          >
            امکانات استاندارد
          </button>
        </div>
      </div>

      {/* Mobile View (Visible only on mobile) */}
      <div className="block md:hidden w-full">
        <MobileComparisonSlider variants={variants} showStandardEquipment={showStandardEquipment} />
      </div>

      {/* Desktop View (Visible only on desktop) */}
      <div className="hidden md:block w-full">
        {/* Grid or Slider depending on length */}
        {variants.length <= 3 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-center">
            {variants.map((car) => (
              <ComparisonCard
                key={car.id}
                car={car}
                showStandardEquipment={showStandardEquipment}
              />
            ))}
          </div>
        ) : (
          <div className="w-full pb-10" dir="ltr">
            <Swiper
              modules={[Navigation, Pagination]}
              spaceBetween={24}
              slidesPerView={1}
              navigation
              pagination={{ clickable: true }}
              breakpoints={{
                640: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
                1280: { slidesPerView: 4 },
              }}
              className="w-full px-2 py-4 pb-12"
            >
              {variants.map((car) => (
                <SwiperSlide key={car.id} className="h-auto">
                  <ComparisonCard
                    car={car}
                    showStandardEquipment={showStandardEquipment}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}
      </div>

    </div>
  );
}
